import React, { useState, useEffect, useRef } from 'react';

const MultiSelectDropdown = ({ label, options, selected, onChange, disabled, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter((o) => o !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const selectAll = () => onChange([...options]);
  const clearAll = () => onChange([]);

  const displayText = disabled
    ? (placeholder || 'Scan first to see options')
    : selected.length === 0
      ? 'All (no filter)'
      : selected.length === options.length
        ? `All ${options.length} selected`
        : `${selected.length} of ${options.length} selected`;

  return (
    <div className="space-y-1.5 min-w-[260px]">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      <div className="relative" ref={ref}>
        <button
          type="button"
          className="w-full min-w-[260px] px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-left text-sm flex justify-between items-center disabled:opacity-50 hover:border-gray-400 transition-colors dark:text-gray-100"
          onClick={() => !disabled && setOpen(!open)}
          disabled={disabled}
        >
          <span>{displayText}</span>
          <span className="text-gray-400 dark:text-slate-500">&#9662;</span>
        </button>
        {open && !disabled && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto min-w-[280px]">
            <div className="p-3 border-b border-gray-100 dark:border-slate-800 flex gap-2">
              <button type="button" className="px-3 py-2 text-sm font-medium bg-blue-50 dark:bg-blue-950 text-blue-700 rounded-lg hover:bg-blue-100" onClick={selectAll}>Select All</button>
              <button type="button" className="px-3 py-2 text-sm font-medium bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600" onClick={clearAll}>Clear All</button>
            </div>
            <div className="p-2">
              {options.map((option) => (
                <div key={option} className="flex items-center gap-3 py-2.5 px-3 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg cursor-pointer" onClick={() => toggleOption(option)}>
                  <input type="checkbox" checked={selected.includes(option)} readOnly className="rounded w-4 h-4" />
                  <label className="cursor-pointer text-sm flex-1">{option}</label>
                </div>
              ))}
              {options.length === 0 && <div className="py-6 text-center text-gray-500 dark:text-slate-400 text-sm">No options available</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
